import { Component, OnInit } from '@angular/core';
import { IFormGroup } from '@shared-libs/interfaces';
import { Action, NavigationDataKey, NavigationType } from '@shared-libs/enums';
import { IReservation } from '@internal-models/reservation.model';
import { NavigationDataManager } from '@shared-managers/navigation-data.manager';
import { EventAttendanceType, EventLocationType, IEvent } from '@internal-models/event.model';
import { ICalendarEvent } from '@shared-models/calendar-event.model';
import { InternalNavigationType } from '@shared-directives/navigate.directive';
import { AttendanceStatus } from '@internal-models/attendance.model';
import { CancelReservationFormFactory } from '@internal-factories/forms/cancel-reservation-form.factory';
import { NavigationService } from '@shared-services/navigation.service';
import { ICancellationReason } from '@internal-models/cancellation-reason.model';
import { CancellationReasonProvider } from '@internal-providers/cancellation-reason.provider';
import { CancellationReasonComponent } from '@internal-components/administration-components/cancellation-reason-components/cancellation-reason-component/cancellation-reason.component';
import { ValidationService } from '@shared-services/validation.service';
import { formIsValid } from '@shared-libs/helpers';

@Component({
	selector: 'cancel-reservation-component',
	templateUrl: 'cancel-reservation.component.html',
	styleUrls: ['./cancel-reservation.component.scss'],
})
export class CancelReservationComponent implements OnInit {
	public cancelForm: IFormGroup<{ CancellationReasonId: string; cancellationInfo: string }>;
	public reservation: IReservation;
	public calendarEvent: ICalendarEvent;
	public type: EventAttendanceType;
	public event: IEvent;
	public cancellationReasons: Array<ICancellationReason>;

	public readonly Action: typeof Action = Action;
	public readonly EventAttendanceType: typeof EventAttendanceType = EventAttendanceType;
	public readonly InternalNavigationType: typeof InternalNavigationType = InternalNavigationType;
	public readonly NavigationDataKey: typeof NavigationDataKey = NavigationDataKey;
	public readonly AttendanceStatus: typeof AttendanceStatus = AttendanceStatus;
	public readonly EventLocationType: typeof EventLocationType = EventLocationType;

	constructor(
		private readonly navigationDataManager: NavigationDataManager,
		private readonly navigationService: NavigationService,
		private readonly cancellationReasonProvider: CancellationReasonProvider,
		private readonly validationService: ValidationService
	) {}

	public async ngOnInit(): Promise<void> {
		this.reservation = this.navigationDataManager.getData(NavigationDataKey.reservation);
		this.type = this.navigationDataManager.getData(NavigationDataKey.eventAttendanceType);
		this.calendarEvent = this.navigationDataManager.getData(NavigationDataKey.calendarEvent);
		this.event = this.navigationDataManager.getData(NavigationDataKey.event);

		this.cancelForm =
			this.navigationDataManager.getData(NavigationDataKey.cancelForm) ?? CancelReservationFormFactory.create();
		void this.getQuestionCategories();
	}

	public async cancelReservation(): Promise<void> {
		if (
			formIsValid(this.cancelForm, CancelReservationComponent) &&
			(await this.validationService.actionIsConfirmed(this.calendarEvent.Event.attendanceType.toUpperCase()))
		) {
			this.navigationService.navigateBack({
				action: Action.DELETE,
				cancellation: {
					CancellationReasonId: this.cancelForm.value.CancellationReasonId,
					cancellationInfo: this.cancelForm.value.cancellationInfo,
				},
				event: this.event,
				calendarEvent: this.calendarEvent,
				reservation: this.reservation,
				eventAttendanceType: this.type,
			});
		}
	}

	public close(): void {
		this.navigationService.navigateBack({
			event: this.event,
			calendarEvent: this.calendarEvent,
			reservation: this.reservation,
			eventAttendanceType: this.type,
		});
	}

	public async createCancellationReason(): Promise<void> {
		void this.navigationService
			.navigateTo<{ cancellationReason: ICancellationReason }>(
				NavigationType.modal,
				CancellationReasonComponent,
				{
					action: Action.CREATE,
				}
			)
			.then((result) => {
				if (result?.cancellationReason) {
					this.cancellationReasons.push(result.cancellationReason);
					this.cancelForm.get('CancellationReasonId').setValue(result.cancellationReason.id);
				}
				this.navigationDataManager.addData(NavigationDataKey.cancelForm, this.cancelForm);
			})
			.catch();
	}

	private async getQuestionCategories(): Promise<void> {
		this.cancellationReasons = await this.cancellationReasonProvider.getAll();
	}
}
